import { pausableWatch } from '@vueuse/core'
import { reactive } from 'vue'

export * from './main'
export * from './partial'

/**
 * Create a hash params based router.
 */
export function createRouter(options: RouterOptions) {
    const {
        delimiter = ':',
        removeNullishValues = true,
        removeFalsyValues = true,
        getParams,
        getQuery,
        pushState
    } = options

    /**
     * Whether the next state should be pushed.
     */
    let shouldPush = true

    /**
     * Current state.
     */
    const state: Record<string, string | string[]> = reactive({})

    /**
     * Get the current params.
     */
    function get() {
        return new URLSearchParams(getParams().split(delimiter).join('&'))
    }

    /**
     * Set the given state.
     */
    function set(params: URLSearchParams, shouldUpdate?: boolean) {
        pause()

        if (shouldUpdate) {
            setState(params)
        }

        if (shouldPush) {
            pushState(getQuery(params, delimiter))
        }

        resume()
    }

    /**
     * Update the state with the given params.
     */
    function setState(params: URLSearchParams) {
        const unusedKeys = new Set(Object.keys(state))
        for (const key of params.keys()) {
            const values = params.getAll(key)
            state[key] = values.length > 1 ? values : values[0] || ''
            unusedKeys.delete(key)
        }
        Array.from(unusedKeys).forEach(key => delete state[key])
    }

    /**
     * On state changed.
     */
    function onStateChanged() {
        const params = new URLSearchParams('')

        Object.keys(state).forEach(key => {
            const mapEntry = state[key]

            if (Array.isArray(mapEntry)) {
                mapEntry.forEach(value => params.append(key, value))
            } else if (removeNullishValues && mapEntry == null) {
                params.delete(key)
            } else if (removeFalsyValues && !mapEntry) {
                params.delete(key)
            } else {
                params.set(key, mapEntry)
            }
        })

        set(params, false)
        shouldPush = true
    }

    /**
     * On route changed.
     */
    function onRouteChanged() {
        shouldPush = false
        set(get(), true)
    }

    /**
     * Watch for changes in the state
     * to update the params.
     */
    const { pause, resume } = pausableWatch(state, onStateChanged, {
        deep: true
    })

    /**
     * Set the initial state.
     */
    setState(get())

    return { state, pause, resume, onRouteChanged }
}

export interface RouterOptions {
    delimiter?: string
    removeNullishValues?: boolean
    removeFalsyValues?: boolean
    getParams: () => string
    getQuery: (params: URLSearchParams, delimiter: string) => string
    pushState: (query: string) => void
}
